import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { FiCheck, FiChevronDown, FiGlobe } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDismiss } from "../../hooks/useDismiss";
import { menuKeys } from "../../hooks/menuKeys";

const Wrap = styled.div`
  position: relative;
`;
const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 44px;
  height: 44px;
  padding: 0 10px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 600;
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};
  svg {
    width: 16px;
    height: 16px;
  }
  &:hover,
  &[aria-expanded="true"] {
    background: ${({ theme }) => theme.colors.raised};
    color: ${({ theme }) => theme.colors.text};
  }
`;
const Menu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  min-width: 180px;
  padding: 6px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.raised};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 16px 40px #0009;
  button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 44px;
    padding: 0 12px;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    &:hover,
    &:focus-visible {
      background: ${({ theme }) => theme.colors.surfaceHover};
    }
    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

/** Показывает текущую локаль и явный выбор языка (design-spec 07). */
export default function LanguageMenu({ align = "right" }: { align?: "right" | "left" }) {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, box, trigger);
  const pick = (l: "ru" | "en") => {
    setLanguage(l);
    close();
    trigger.current?.focus();
  };
  return (
    <Wrap ref={box}>
      <Trigger
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t("header.language")}: ${language.toUpperCase()}`}
        onClick={() => setOpen((v) => !v)}
      >
        <FiGlobe aria-hidden="true" />
        {language.toUpperCase()}
        <FiChevronDown aria-hidden="true" />
      </Trigger>
      {open && (
        <Menu
          role="menu"
          aria-label={t("header.language")}
          style={align === "left" ? { left: 0, right: "auto" } : undefined}
          onKeyDown={menuKeys}
          ref={(el) => el?.querySelector<HTMLElement>("button")?.focus()}
        >
          {(["ru", "en"] as const).map((l) => (
            <button key={l} type="button" role="menuitemradio" aria-checked={language === l} onClick={() => pick(l)}>
              {t(`lang.${l}`)}
              {language === l && <FiCheck aria-hidden="true" />}
            </button>
          ))}
        </Menu>
      )}
    </Wrap>
  );
}
